import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import StandardModal from "./StandardModal";
import { usePreventScrollShift } from "./hooks/usePreventScrollShift";
import {
  Scale,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Eye,
  Search,
  Filter,
  Calendar,
  DollarSign,
  FileText,
  User,
  Clock,
  ChevronDown,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Info,
  Users,
  Building2,
  Wallet,
  ArrowUp,
  ArrowDown,
  Package,
} from "lucide-react";

// ✅ CONFIGURACIÓN ANTI-ZOOM OBLIGATORIA
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// ✅ COLORES DE VISTA ARQUEOS
const getVistaColor = () => ({
  gradient: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
  solid: "#2563eb",
  light: "#2563eb10",
  description: "Ajustes de caja",
});

// Funciones helper para manejar fechas sin problemas de timezone
const formatDateForInput = (dateString) => {
  if (!dateString) return new Date().toISOString().split("T")[0];
  if (dateString.includes("T")) {
    return dateString.split("T")[0];
  }
  return dateString;
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";

  // Tomar directamente los componentes del string YYYY-MM-DD
  const [year, month, day] = dateString.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

const formatMonthForSelect = (monthString) => {
  // monthString formato: "2025-01"
  const [year, month] = monthString.split("-");
  const date = new Date(year, month - 1, 15); // Día 15 para evitar problemas de timezone
  return new Intl.DateTimeFormat("es-ES", {
    year: "numeric",
    month: "long",
  }).format(date);
};

// ✅ DROPDOWN PERSONALIZADO UNIFICADO
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "14px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#3b82f6";
          e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#f1f5f9";
          e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#eff6ff" : "transparent",
                color: option.value === value ? "#3b82f6" : "#374151",
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : "400",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "#f8fafc";
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "transparent";
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Modal personalizado para alertas/confirmaciones
function CustomModal({
  isOpen,
  onClose,
  type = "info",
  title,
  message,
  onConfirm,
  confirmText = "Aceptar",
  cancelText = "Cancelar",
  isMobile,
}) {
  if (!isOpen) return null;

  const getColors = () => {
    switch (type) {
      case "confirm":
        return {
          bg: "#fef3cd",
          border: "#f59e0b",
          text: "#92400e",
          button: "#f59e0b",
          icon: AlertCircle,
        };
      case "error":
        return {
          bg: "#fef2f2",
          border: "#ef4444",
          text: "#dc2626",
          button: "#ef4444",
          icon: XCircle,
        };
      case "success":
        return {
          bg: "#f0fdf4",
          border: "#22c55e",
          text: "#166534",
          button: "#22c55e",
          icon: CheckCircle2,
        };
      default:
        return {
          bg: "#f0f9ff",
          border: "#3b82f6",
          text: "#1d4ed8",
          button: "#3b82f6",
          icon: Info,
        };
    }
  };

  const colors = getColors();
  const IconComponent = colors.icon;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
        padding: "20px",
        animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "100%",
          maxWidth: isMobile ? "90%" : "400px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          border: `2px solid ${colors.border}`,
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <IconComponent size={32} color={colors.text} />
          </div>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: colors.text,
            }}
          >
            {title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "#64748b",
              lineHeight: "1.4",
            }}
          >
            {message}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: type === "confirm" ? "space-between" : "center",
          }}
        >
          {type === "confirm" && (
            <button
              onClick={onClose}
              className="button-hover"
              style={{
                flex: 1,
                padding: "12px 20px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                background: "white",
                color: "#64748b",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={() => {
              if (type === "confirm" && onConfirm) {
                onConfirm();
              }
              onClose();
            }}
            className="button-hover"
            style={{
              flex: type === "confirm" ? 1 : "auto",
              padding: "12px 20px",
              border: "none",
              borderRadius: "8px",
              background: colors.button,
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              minWidth: type !== "confirm" ? "120px" : "auto",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal para vista completa del arqueo
function ArqueoDetailModal({
  arqueo,
  proyectos,
  cajas,
  constructora,
  onClose,
  isMobile,
}) {
  const caja = cajas.find((c) => c.id === arqueo.caja_id);
  const proyecto = proyectos.find((p) => p.id === caja?.proyecto_id);
  const colorVista = getVistaColor();

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
        animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: colorVista.gradient,
            color: "white",
            padding: isMobile ? "20px" : "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Eye size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                style={{
                  margin: "0 0 4px 0",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Vista Detallada - Arqueo
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  opacity: 0.9,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {caja?.nombre} - {formatDateForDisplay(arqueo.fecha)}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="button-hover"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "10px",
              padding: "8px 12px",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "600",
              flexShrink: 0,
              minWidth: "max-content",
              whiteSpace: "nowrap",
              touchAction: "manipulation",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "30px",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "24px",
            }}
          >
            {/* Información Principal */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Scale size={20} color={colorVista.solid} />
                Información Principal
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Caja */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                    }}
                  >
                    Caja
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    {caja?.nombre} {caja?.moneda}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#64748b",
                    }}
                  >
                    {caja?.tipo_caja === "empresa"
                      ? "Caja de Empresa"
                      : `Proyecto: ${proyecto?.codigo}`}
                  </p>
                  <p
                    style={{
                      margin: "4px 0 0 0",
                      fontSize: "13px",
                      color: "#059669",
                      fontWeight: "500",
                    }}
                  >
                    Saldo actual: $
                    {parseFloat(caja?.balance_actual || 0).toLocaleString()}
                  </p>
                </div>

                {/* Tipo de Ajuste */}
                <div
                  style={{
                    background:
                      parseFloat(arqueo.monto_ajuste) >= 0
                        ? "#dcfce7"
                        : "#fef2f2",
                    padding: "16px",
                    borderRadius: "12px",
                    border: `2px solid ${
                      parseFloat(arqueo.monto_ajuste) >= 0
                        ? "#bbf7d0"
                        : "#fecaca"
                    }`,
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color:
                        parseFloat(arqueo.monto_ajuste) >= 0
                          ? "#166534"
                          : "#991b1b",
                      textTransform: "uppercase",
                    }}
                  >
                    Tipo de Ajuste
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color:
                        parseFloat(arqueo.monto_ajuste) >= 0
                          ? "#166534"
                          : "#dc2626",
                    }}
                  >
                    {parseFloat(arqueo.monto_ajuste) >= 0 ? "Suma" : "Resta"}
                  </p>
                </div>

                {/* Monto */}
                <div
                  style={{
                    background: `linear-gradient(135deg, ${
                      parseFloat(arqueo.monto_ajuste) >= 0
                        ? "#dcfce7, #bbf7d0"
                        : "#fef2f2, #fecaca"
                    })`,
                    padding: "20px",
                    borderRadius: "12px",
                    border: `2px solid ${
                      parseFloat(arqueo.monto_ajuste) >= 0
                        ? "#10b981"
                        : "#ef4444"
                    }`,
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color:
                        parseFloat(arqueo.monto_ajuste) >= 0
                          ? "#166534"
                          : "#991b1b",
                      textTransform: "uppercase",
                    }}
                  >
                    Monto del Ajuste
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: isMobile ? "24px" : "28px",
                      fontWeight: "800",
                      color:
                        parseFloat(arqueo.monto_ajuste) >= 0
                          ? "#10b981"
                          : "#ef4444",
                    }}
                  >
                    {parseFloat(arqueo.monto_ajuste) >= 0 ? "+" : ""}
                    {Number(arqueo.monto_ajuste).toLocaleString()}{" "}
                    {caja?.moneda}
                  </p>
                  {arqueo.tipo_cambio &&
                    parseFloat(arqueo.tipo_cambio) !== 1 && (
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          color: "#64748b",
                        }}
                      >
                        Tipo de cambio: ${arqueo.tipo_cambio}
                      </p>
                    )}
                </div>
              </div>
            </div>

            {/* Información Adicional */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FileText size={20} color={colorVista.solid} />
                Detalles Adicionales
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Fecha */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                    }}
                  >
                    Fecha del Arqueo
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    {formatDateForDisplay(arqueo.fecha)}
                  </p>
                </div>

                {/* Proyecto relacionado si no es empresa */}
                {caja?.tipo_caja !== "empresa" && proyecto && (
                  <div
                    style={{
                      background: "#eff6ff",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "2px solid #dbeafe",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#1e40af",
                        textTransform: "uppercase",
                      }}
                    >
                      Proyecto Relacionado
                    </p>
                    <p
                      style={{
                        margin: "0 0 4px 0",
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#1d4ed8",
                      }}
                    >
                      <Building2
                        size={16}
                        color="#1d4ed8"
                        style={{ display: "inline", marginRight: "8px" }}
                      />
                      {proyecto.codigo}
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#64748b",
                      }}
                    >
                      Cliente: {proyecto.apellido_cliente}
                    </p>
                  </div>
                )}

                {/* Descripción */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                    }}
                  >
                    Descripción / Motivo
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#1e293b",
                      lineHeight: "1.5",
                    }}
                  >
                    {arqueo.descripcion || "Sin descripción proporcionada"}
                  </p>
                </div>

                {/* Equivalencia en otra moneda si aplica */}
                {arqueo.tipo_cambio && parseFloat(arqueo.tipo_cambio) !== 1 && (
                  <div
                    style={{
                      background: "#fefbf3",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "2px solid #fbbf24",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#92400e",
                        textTransform: "uppercase",
                      }}
                    >
                      Equivalencia
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#f59e0b",
                      }}
                    >
                      {caja?.moneda === "USD"
                        ? `≈ ARS $${(
                            parseFloat(arqueo.monto_ajuste) *
                            parseFloat(arqueo.tipo_cambio)
                          ).toLocaleString()}`
                        : `≈ USD $${(
                            parseFloat(arqueo.monto_ajuste) /
                            parseFloat(arqueo.tipo_cambio)
                          ).toLocaleString()}`}
                    </p>
                  </div>
                )}

                {/* Información del sistema */}
                <div
                  style={{
                    background: "#f1f5f9",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #cbd5e1",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#475569",
                      textTransform: "uppercase",
                    }}
                  >
                    Información del Sistema
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "13px",
                      color: "#64748b",
                    }}
                  >
                    Creado:{" "}
                    {new Date(arqueo.created_at).toLocaleString("es-ES")}
                  </p>
                  {arqueo.updated_at &&
                    arqueo.updated_at !== arqueo.created_at && (
                      <p
                        style={{
                          margin: 0,
                          fontSize: "13px",
                          color: "#64748b",
                        }}
                      >
                        Modificado:{" "}
                        {new Date(arqueo.updated_at).toLocaleString("es-ES")}
                      </p>
                    )}
                </div>
              </div>
            </div>
          </div>

          {/* Resumen del impacto */}
          <div
            style={{
              marginTop: "32px",
              padding: "20px",
              background:
                parseFloat(arqueo.monto_ajuste) >= 0 ? "#f0fdf4" : "#fef2f2",
              borderRadius: "12px",
              border: `2px solid ${
                parseFloat(arqueo.monto_ajuste) >= 0 ? "#bbf7d0" : "#fecaca"
              }`,
            }}
          >
            <h4
              style={{
                margin: "0 0 12px 0",
                fontSize: "16px",
                fontWeight: "600",
                color:
                  parseFloat(arqueo.monto_ajuste) >= 0 ? "#166534" : "#991b1b",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <DollarSign size={16} />
              Resumen del Impacto
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "12px",
                fontSize: "14px",
              }}
            >
              <div>
                <span style={{ color: "#64748b" }}>
                  Balance antes del ajuste:
                </span>
                <div style={{ fontWeight: "600", color: "#1e293b" }}>
                  {caja?.moneda} $
                  {(
                    parseFloat(caja?.balance_actual || 0) -
                    parseFloat(arqueo.monto_ajuste)
                  ).toLocaleString()}
                </div>
              </div>
              <div>
                <span style={{ color: "#64748b" }}>Balance actual:</span>
                <div
                  style={{
                    fontWeight: "600",
                    color:
                      parseFloat(arqueo.monto_ajuste) >= 0
                        ? "#166534"
                        : "#991b1b",
                  }}
                >
                  {caja?.moneda} $
                  {parseFloat(caja?.balance_actual || 0).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Arqueos({ constructora, usuario }) {
  const [arqueos, setArqueos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showModal, setShowModal] = useState(false);
  const [editingArqueo, setEditingArqueo] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProject, setFilterProject] = useState(() => {
    // Intentar restaurar el último filtro de proyecto desde localStorage
    try {
      const saved = localStorage.getItem('arqueos_filterProject');
      return saved || "todos";
    } catch (error) {
      console.error('Error restaurando filtro de proyecto:', error);
      return "todos";
    }
  });
  const [filterMonth, setFilterMonth] = useState("todos");
  const [filterEstado, setFilterEstado] = useState("activos");
  const [filterTipo, setFilterTipo] = useState("todos");

  // Estados para modales
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedArqueoDetail, setSelectedArqueoDetail] = useState(null);

  // Estados para alertas personalizadas
  const [customModal, setCustomModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
  });

  const colorVista = getVistaColor();

  // ✅ SCROLL RESET OBLIGATORIO al cambiar vista
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ✅ ESCAPE handler obligatorio para cerrar modales
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowModal(false);
        setShowDetailModal(false);
        setCustomModal((prev) => ({ ...prev, isOpen: false }));
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (constructora?.id) {
      cargarDatos();
    }
  }, [constructora, filterEstado]);

  // Función para guardar el filtro de proyecto en localStorage
  const guardarFiltroProyecto = (filtro) => {
    try {
      localStorage.setItem('arqueos_filterProject', filtro);
    } catch (error) {
      console.error('Error guardando filtro de proyecto:', error);
    }
  };

  const showCustomAlert = (type, title, message, onConfirm = null) => {
    setCustomModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
    });
  };

  // Función para abrir vista detallada
  const openDetailModal = (arqueo) => {
    setSelectedArqueoDetail(arqueo);
    setShowDetailModal(true);
  };

  const cargarDatos = async () => {
    try {
      // Cargar arqueos con datos relacionados
      let query = supabase
        .from("arqueos_caja")
        .select(
          `
          *,
          cajas (
            id,
            tipo_caja,
            moneda,
            nombre,
            balance_actual,
            proyectos (id, codigo)
          )
        `
        )
        .eq("constructora_id", constructora.id);

      if (filterEstado === "activos") {
        query = query.eq("activo", true).is("deleted_at", null);
      } else if (filterEstado === "eliminados") {
        query = query.or("activo.eq.false,deleted_at.not.is.null");
      }

      const { data: arqueosData, error: arqueosError } = await query.order(
        "created_at",
        { ascending: false }
      );

      if (arqueosError) {
        console.error("Error cargando arqueos:", arqueosError);
      } else {
        setArqueos(arqueosData || []);
      }

      // Cargar TODOS los proyectos (activos y finalizados)
      let proyectosData = [];
      let proyectosError = null;

      if (filterEstado === "eliminados") {
        // proyectos con arqueos eliminados: buscar cajas del proyecto con arqueos eliminados
        const { data: idsData, error: idsError } = await supabase
          .from("arqueos_caja")
          .select("caja_id")
          .eq("constructora_id", constructora.id)
          .or("activo.eq.false,deleted_at.not.is.null");

        if (idsError) {
          proyectosError = idsError;
        } else {
          const cajaIds = Array.from(
            new Set((idsData || []).map((r) => r.caja_id).filter(Boolean))
          );
          if (cajaIds.length > 0) {
            const { data: cajasRows, error: cajasErr } = await supabase
              .from("cajas")
              .select("proyecto_id")
              .in("id", cajaIds);
            if (cajasErr) {
              proyectosError = cajasErr;
            } else {
              const projIds = Array.from(
                new Set(
                  (cajasRows || []).map((r) => r.proyecto_id).filter(Boolean)
                )
              );
              if (projIds.length > 0) {
                const { data, error } = await supabase
                  .from("proyectos")
                  .select("*")
                  .eq("constructora_id", constructora.id)
                  .in("id", projIds)
                  .order("codigo");
                proyectosData = data || [];
                proyectosError = error || null;
              } else {
                proyectosData = [];
              }
            }
          } else {
            proyectosData = [];
          }
        }
      } else {
        const { data, error } = await supabase
          .from("proyectos")
          .select("*")
          .eq("constructora_id", constructora.id)
          .eq("activo", true)
          .is("deleted_at", null)
          .order("codigo");
        proyectosData = data || [];
        proyectosError = error || null;
      }

      if (proyectosError) {
        console.error("Error cargando proyectos:", proyectosError);
      } else {
        setProyectos(proyectosData || []);
      }

      // Cargar todas las cajas con balance actual
      const { data: cajasData, error: cajasError } = await supabase
        .from("cajas")
        .select(
          `
          *,
          proyectos (id, codigo)
        `
        )
        .eq("constructora_id", constructora.id)
        .order("tipo_caja");

      if (cajasError) {
        console.error("Error cargando cajas:", cajasError);
      } else {
        setCajas(cajasData || []);
      }
    } catch (err) {
      console.error("Error:", err);
    }
    setLoading(false);
  };

  // Filtrar arqueos
  const filteredArqueos = arqueos.filter((arqueo) => {
    const matchesSearch =
      arqueo.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      arqueo.cajas?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      arqueo.cajas?.proyectos?.codigo
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesProject =
      filterProject === "todos" ||
      arqueo.cajas?.proyectos?.id === filterProject ||
      (filterProject === "empresa" && arqueo.cajas?.tipo_caja === "empresa");

    const matchesMonth =
      filterMonth === "todos" || arqueo.fecha.substring(0, 7) === filterMonth;

    const matchesTipo =
      filterTipo === "todos" ||
      (filterTipo === "suma" && parseFloat(arqueo.monto_ajuste) > 0) ||
      (filterTipo === "resta" && parseFloat(arqueo.monto_ajuste) < 0);

    return matchesSearch && matchesProject && matchesMonth && matchesTipo;
  });

  const handleEliminar = async (id) => {
    showCustomAlert(
      "confirm",
      "¿Eliminar arqueo?",
      "Se revertirá el ajuste en la caja y el arqueo quedará eliminado (recuperable). ¿Continuar?",
      async () => {
        try {
                  const { error } = await supabase.rpc("rpc_soft_delete_arqueo", {
          p_arqueo_id: id,
          p_usuario_id: usuario.id,
          p_razon: null,
        });
          if (error) {
            showCustomAlert(
              "error",
              "Error",
              "Error al eliminar: " + error.message
            );
          } else {
            await cargarDatos();
            showCustomAlert(
              "success",
              "Éxito",
              "Arqueo eliminado correctamente"
            );
          }
        } catch (err) {
          showCustomAlert("error", "Error", "Error: " + err.message);
        }
      }
    );
  };

  const handleRestaurar = async (id) => {
    showCustomAlert(
      "confirm",
      "¿Restaurar arqueo?",
      "Se re-aplicará el ajuste en la caja. ¿Continuar?",
      async () => {
        try {
                  const { error } = await supabase.rpc("rpc_restore_arqueo", {
          p_arqueo_id: id,
          p_usuario_id: usuario.id,
        });
          if (error) {
            showCustomAlert(
              "error",
              "Error",
              "Error al restaurar: " + error.message
            );
          } else {
            await cargarDatos();
            showCustomAlert(
              "success",
              "Éxito",
              "Arqueo restaurado correctamente"
            );
          }
        } catch (err) {
          showCustomAlert("error", "Error", "Error: " + err.message);
        }
      }
    );
  };

  // Obtener meses únicos para el filtro
  const mesesUnicos = [...new Set(arqueos.map((a) => a.fecha.substring(0, 7)))]
    .sort()
    .reverse();

  // ✅ PREPARAR OPCIONES PARA DROPDOWNS
  const proyectosOptions = [
    { value: "todos", label: "Todos los proyectos" },
    { value: "empresa", label: "Cajas de Empresa" },
    ...proyectos.map((proyecto) => ({
      value: proyecto.id,
      label: `${proyecto.codigo} - ${proyecto.apellido_cliente}`,
    })),
  ];

  const mesesOptions = [
    { value: "todos", label: "Todos los meses" },
    ...mesesUnicos.map((mes) => ({
      value: mes,
      label: formatMonthForSelect(mes),
    })),
  ];

  const tiposOptions = [
    { value: "todos", label: "Todos los tipos" },
    { value: "suma", label: "Solo Sumas (+)" },
    { value: "resta", label: "Solo Restas (-)" },
  ];

  // ✅ STATS DATA PARA RESUMEN COMPACTO
  const statsData = [
    {
      label: "Total Arqueos",
      value: filteredArqueos.length.toString(),
      icon: Scale,
      color: colorVista.solid,
    },
    {
      label: "Este Mes",
      value: filteredArqueos
        .filter(
          (a) =>
            a.fecha.substring(0, 7) === new Date().toISOString().substring(0, 7)
        )
        .length.toString(),
      icon: Calendar,
      color: "#0ea5e9",
    },
    {
      label: "Sumas (+)",
      value: filteredArqueos
        .filter((a) => parseFloat(a.monto_ajuste) > 0)
        .length.toString(),
      icon: DollarSign,
      color: "#10b981",
    },
    {
      label: "Restas (-)",
      value: filteredArqueos
        .filter((a) => parseFloat(a.monto_ajuste) < 0)
        .length.toString(),
      icon: DollarSign,
      color: "#ef4444",
    },
  ];

  // Prevenir scroll shift cuando el modal está abierto
  usePreventScrollShift(showModal);

  return (
    <div
      className="main-container"
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* ✅ HEADER UNIFICADO CON FILTROS */}
      <div
        className="header-section"
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
            minHeight: "56px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: "200px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: colorVista.gradient,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 16px ${colorVista.light}`,
                flexShrink: 0,
              }}
            >
              <Scale size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Arqueos de Caja
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "400",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Gestioná ajustes de balances de {constructora?.nombre}
              </p>
            </div>
          </div>

          {/* Botón desktop unificado - SIEMPRE VISIBLE */}
          {!isMobile && (
            <button
              onClick={() => {
                setEditingArqueo(null);
                setShowModal(true);
              }}
              className="button-hover"
              style={{
                background: colorVista.gradient,
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: `0 4px 16px ${colorVista.light}`,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
                flexShrink: 0,
                minWidth: "max-content",
                whiteSpace: "nowrap",
              }}
            >
              <Plus size={16} />
              Nuevo Arqueo
            </button>
          )}
        </div>

        {/* ✅ FILTROS UNIFICADOS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 1fr 1fr 1fr",
            gap: "16px",
            marginBottom: "20px",
          }}
        >
          <div style={{ position: "relative" }}>
            <Search
              size={16}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#64748b",
                zIndex: 1,
              }}
            />
            <input
              type="text"
              placeholder="Buscar por proyecto, caja o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus={false}
              autoComplete="off"
              style={{
                width: "100%",
                padding: "14px 16px 14px 44px",
                border: "2px solid #f1f5f9",
                borderRadius: "12px",
                fontSize: "16px",
                WebkitAppearance: "none",
                appearance: "none",
                background: "white",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                outline: "none",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#f1f5f9";
                e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
              }}
            />
          </div>

          <CustomDropdown
            value={filterProject}
            onChange={(value) => {
              setFilterProject(value);
              guardarFiltroProyecto(value);
            }}
            options={proyectosOptions}
            placeholder="Proyecto"
            icon={Building2}
          />

          <CustomDropdown
            value={filterMonth}
            onChange={setFilterMonth}
            options={mesesOptions}
            placeholder="Mes"
            icon={Calendar}
          />
          <CustomDropdown
            value={filterEstado}
            onChange={setFilterEstado}
            options={[
              { value: "activos", label: "Activos" },
              { value: "eliminados", label: "Eliminados" },
            ]}
            placeholder="Estado"
            icon={Filter}
          />

          <CustomDropdown
            value={filterTipo}
            onChange={setFilterTipo}
            options={tiposOptions}
            placeholder="Tipo"
            icon={Filter}
          />
        </div>
      </div>

      {/* ✅ STATS COMPACTAS (SOLO DESKTOP) */}
      {!isMobile && (
        <div
          className="stats-section"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "12px",
            marginBottom: "20px",
            animation: "fadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {statsData.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="stat-card"
                style={{
                  background: "white",
                  padding: "12px",
                  borderRadius: "12px",
                  textAlign: "center",
                  border: "1px solid #f1f5f9",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  minHeight: "80px",
                  animation: `slideInRight 0.${
                    8 + index
                  }s cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    background: `${stat.color}10`,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                  }}
                >
                  <IconComponent size={16} color={stat.color} />
                </div>
                <p
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    margin: "0 0 4px 0",
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {stat.label}
                </p>
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#1e293b",
                    margin: 0,
                  }}
                >
                  {stat.value}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* ✅ CONTENIDO PRINCIPAL */}
      <div
        className="main-content"
        style={{
          animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "80px 20px",
              background: "white",
              borderRadius: "16px",
              border: "1px solid #f1f5f9",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  border: "2px solid #e2e8f0",
                  borderTop: "2px solid #3b82f6",
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                  animation: "spin 1s linear infinite",
                }}
              />
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                Cargando...
              </p>
            </div>
          </div>
        ) : filteredArqueos.length === 0 ? (
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: isMobile ? "40px 20px" : "60px 40px",
              textAlign: "center",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              // border eliminado para quitar la línea punteada
            }}
          >
            <Scale size={48} color="#64748b" style={{ marginBottom: "20px" }} />
            <h3
              style={{
                fontSize: isMobile ? "18px" : "20px",
                color: "#1e293b",
                marginBottom: "12px",
                fontWeight: "700",
              }}
            >
              {searchTerm ||
              filterProject !== "todos" ||
              filterMonth !== "todos" ||
              filterTipo !== "todos"
                ? "No se encontraron arqueos"
                : "No hay arqueos aún"}
            </h3>
            <p
              style={{
                color: "#64748b",
                fontSize: "16px",
                marginBottom:
                  searchTerm ||
                  filterProject !== "todos" ||
                  filterMonth !== "todos" ||
                  filterTipo !== "todos"
                    ? "0"
                    : "32px",
                lineHeight: "1.5",
              }}
            >
              {searchTerm ||
              filterProject !== "todos" ||
              filterMonth !== "todos" ||
              filterTipo !== "todos"
                ? "Intenta cambiar los filtros de búsqueda"
                : "Los arqueos aparecerán cuando se registre el primer ajuste de caja"}
            </p>
            {!searchTerm &&
              filterProject === "todos" &&
              filterMonth === "todos" &&
              filterTipo === "todos" && (
                <button
                  onClick={() => {
                    setEditingArqueo(null);
                    setShowModal(true);
                  }}
                  className="button-hover"
                  style={{
                    background: colorVista.gradient,
                    color: "white",
                    border: "none",
                    padding: "14px 28px",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    margin: "0 auto",
                    boxShadow: `0 8px 32px ${colorVista.light}`,
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                >
                  <Plus size={16} />
                  Crear Primer Arqueo
                </button>
              )}
          </div>
        ) : isMobile ? (
          // ✅ CARDS MÓVILES ESTANDARIZADAS
          <div style={{ padding: "8px" }}>
            {filteredArqueos.map((arqueo, index) => {
              const caja = cajas.find((c) => c.id === arqueo.caja_id);
              const proyecto = proyectos.find(
                (p) => p.id === caja?.proyecto_id
              );

              return (
                <div
                  key={arqueo.id}
                  onClick={() => openDetailModal(arqueo)}
                  style={{
                    background: "white",
                    border: "1px solid #f1f5f9",
                    borderRadius: "12px",
                    padding: "16px",
                    marginBottom: "8px",
                    cursor: "pointer",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    animation: `slideInRight 0.${
                      5 + (index % 10)
                    }s cubic-bezier(0.4, 0, 0.2, 1)`,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    touchAction: "manipulation",
                  }}
                  onTouchStart={(e) => {
                    e.currentTarget.style.transform = "scale(0.98)";
                    e.currentTarget.style.backgroundColor = "#f8fafc";
                  }}
                  onTouchEnd={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.backgroundColor = "white";
                  }}
                >
                  {/* HEADER UNIFICADO */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "12px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#1e293b",
                          marginBottom: "4px",
                        }}
                      >
                        {caja?.nombre} {caja?.moneda}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          fontWeight: "500",
                        }}
                      >
                        {caja?.tipo_caja === "empresa"
                          ? "Empresa"
                          : `Proyecto ${proyecto?.codigo}`}
                      </div>
                    </div>
                    <div
                      style={{
                        textAlign: "right",
                        marginLeft: "12px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "800",
                          color:
                            parseFloat(arqueo.monto_ajuste) >= 0
                              ? "#10b981"
                              : "#ef4444",
                          marginBottom: "2px",
                        }}
                      >
                        {parseFloat(arqueo.monto_ajuste) >= 0 ? "+" : ""}
                        {Number(arqueo.monto_ajuste).toLocaleString()}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#64748b",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "4px",
                        }}
                      >
                        <Calendar size={10} />
                        {formatDateForDisplay(arqueo.fecha)}
                      </div>
                    </div>
                  </div>

                  {/* BADGES UNIFICADOS */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginBottom: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        background:
                          parseFloat(arqueo.monto_ajuste) >= 0
                            ? "#dcfce7"
                            : "#fef2f2",
                        color:
                          parseFloat(arqueo.monto_ajuste) >= 0
                            ? "#166534"
                            : "#dc2626",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                      }}
                    >
                      {parseFloat(arqueo.monto_ajuste) >= 0 ? "Suma" : "Resta"}
                    </span>
                    {arqueo.tipo_cambio &&
                      parseFloat(arqueo.tipo_cambio) !== 1 && (
                        <span
                          style={{
                            background: "#fefbf3",
                            color: "#92400e",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "9px",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                          }}
                        >
                          <DollarSign size={8} />
                          TC: ${arqueo.tipo_cambio}
                        </span>
                      )}
                  </div>

                  {/* DESCRIPCIÓN (si existe) */}
                  {arqueo.descripcion && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                        marginBottom: "12px",
                        lineHeight: "1.4",
                      }}
                    >
                      {arqueo.descripcion}
                    </div>
                  )}

                  {/* FOOTER UNIFICADO */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingTop: "12px",
                      borderTop: "1px solid #f1f5f9",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#059669",
                        fontWeight: "500",
                      }}
                    >
                      Saldo: $
                      {parseFloat(caja?.balance_actual || 0).toLocaleString()}
                    </div>

                    <div style={{ display: "flex", gap: "4px" }}>
                      {filterEstado === "activos" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingArqueo(arqueo);
                            setShowModal(true);
                          }}
                          style={{
                            background: "#eff6ff",
                            border: "none",
                            borderRadius: "6px",
                            padding: "6px 8px",
                            cursor: "pointer",
                            color: "#3b82f6",
                            touchAction: "manipulation",
                          }}
                        >
                          <Edit size={12} />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (filterEstado === "activos")
                            handleEliminar(arqueo.id);
                          else handleRestaurar(arqueo.id);
                        }}
                        style={{
                          background:
                            filterEstado === "activos" ? "#fef2f2" : "#f0fdf4",
                          border: "none",
                          borderRadius: "6px",
                          padding: "6px 8px",
                          cursor: "pointer",
                          color:
                            filterEstado === "activos" ? "#ef4444" : "#22c55e",
                          touchAction: "manipulation",
                        }}
                      >
                        {filterEstado === "activos" ? (
                          <Trash2 size={12} />
                        ) : (
                          <Package size={12} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // ✅ TABLA DESKTOP UNIFICADA
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              border: "1px solid #f1f5f9",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  minWidth: "900px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      Fecha
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      Caja
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "right",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      Ajuste
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      Descripción
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "14px",
                        width: "160px",
                      }}
                    >
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArqueos.map((arqueo, index) => {
                    const caja = cajas.find((c) => c.id === arqueo.caja_id);
                    const proyecto = proyectos.find(
                      (p) => p.id === caja?.proyecto_id
                    );

                    return (
                      <tr
                        key={arqueo.id}
                        style={{
                          borderBottom:
                            index < filteredArqueos.length - 1
                              ? "1px solid #f1f5f9"
                              : "none",
                          transition: "background-color 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.target.parentElement.style.backgroundColor =
                            "#f8fafc";
                        }}
                        onMouseLeave={(e) => {
                          e.target.parentElement.style.backgroundColor =
                            "transparent";
                        }}
                      >
                        <td
                          style={{
                            padding: "16px 20px",
                            color: "#374151",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          {formatDateForDisplay(arqueo.fecha)}
                        </td>
                        <td
                          style={{
                            padding: "16px 20px",
                            color: "#1e293b",
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          <div>
                            <div>
                              {caja?.nombre} {caja?.moneda}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#64748b",
                                fontWeight: "400",
                              }}
                            >
                              {caja?.tipo_caja === "empresa"
                                ? "Empresa"
                                : `Proyecto ${proyecto?.codigo}`}
                            </div>
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#059669",
                                fontWeight: "500",
                              }}
                            >
                              Saldo actual: $
                              {parseFloat(
                                caja?.balance_actual || 0
                              ).toLocaleString()}
                            </div>
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "16px 20px",
                            textAlign: "right",
                            fontSize: "14px",
                            fontWeight: "700",
                            color:
                              parseFloat(arqueo.monto_ajuste) >= 0
                                ? "#059669"
                                : "#dc2626",
                          }}
                        >
                          {parseFloat(arqueo.monto_ajuste) >= 0 ? "+" : ""}
                          {Number(arqueo.monto_ajuste).toLocaleString()}
                          <div
                            style={{
                              fontSize: "10px",
                              color: "#64748b",
                              fontWeight: "400",
                            }}
                          >
                            {caja?.moneda}
                          </div>
                          {/* Mostrar tipo de cambio si existe */}
                          {arqueo.tipo_cambio &&
                            parseFloat(arqueo.tipo_cambio) !== 1 && (
                              <div
                                style={{
                                  fontSize: "9px",
                                  color: "#0ea5e9",
                                  fontWeight: "400",
                                }}
                              >
                                TC: ${arqueo.tipo_cambio}
                              </div>
                            )}
                        </td>
                        <td
                          style={{
                            padding: "16px 20px",
                            color: "#64748b",
                            fontSize: "13px",
                            maxWidth: "250px",
                          }}
                        >
                          <div
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {arqueo.descripcion || "-"}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "16px 20px",
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "6px",
                              justifyContent: "center",
                            }}
                          >
                            {/* Botón para ver detalles completos */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetailModal(arqueo);
                              }}
                              className="action-button"
                              style={{
                                background: "#eff6ff",
                                border: "none",
                                borderRadius: "6px",
                                padding: "6px 8px",
                                cursor: "pointer",
                                color: "#1d4ed8",
                                fontSize: "12px",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = "#dbeafe";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = "#eff6ff";
                              }}
                            >
                              <Eye size={12} />
                            </button>

                            {filterEstado === "activos" && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingArqueo(arqueo);
                                  setShowModal(true);
                                }}
                                className="action-button"
                                style={{
                                  background: "#f1f5f9",
                                  border: "none",
                                  borderRadius: "6px",
                                  padding: "6px 8px",
                                  cursor: "pointer",
                                  color: "#475569",
                                  fontSize: "12px",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = "#e2e8f0";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = "#f1f5f9";
                                }}
                              >
                                <Edit size={12} />
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (filterEstado === "activos")
                                  handleEliminar(arqueo.id);
                                else handleRestaurar(arqueo.id);
                              }}
                              className="action-button"
                              style={{
                                background:
                                  filterEstado === "activos"
                                    ? "#fef2f2"
                                    : "#f0fdf4",
                                border: "none",
                                borderRadius: "6px",
                                padding: "6px 8px",
                                cursor: "pointer",
                                color:
                                  filterEstado === "activos"
                                    ? "#dc2626"
                                    : "#16a34a",
                                fontSize: "12px",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background =
                                  filterEstado === "activos"
                                    ? "#fee2e2"
                                    : "#dcfce7";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background =
                                  filterEstado === "activos"
                                    ? "#fef2f2"
                                    : "#f0fdf4";
                              }}
                            >
                              {filterEstado === "activos" ? (
                                <Trash2 size={12} />
                              ) : (
                                <Package size={12} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ✅ BOTÓN FLOTANTE MÓVIL UNIFICADO */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            bottom: "100px",
            right: "20px",
            zIndex: 1000,
          }}
        >
          <button
            onClick={() => {
              setEditingArqueo(null);
              setShowModal(true);
            }}
            className="button-hover"
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "28px",
              background: colorVista.gradient,
              color: "white",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              boxShadow: `0 8px 32px ${colorVista.light}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
            onTouchStart={(e) => {
              e.currentTarget.style.transform = "scale(0.95)";
            }}
            onTouchEnd={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <Plus size={24} />
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <ArqueoModal
          constructora={constructora}
          usuario={usuario}
          arqueo={editingArqueo}
          proyectos={proyectos}
          cajas={cajas}
          onClose={() => {
            setShowModal(false);
            setEditingArqueo(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEditingArqueo(null);
            cargarDatos();
            showCustomAlert(
              "success",
              "Éxito",
              editingArqueo
                ? "Arqueo actualizado correctamente"
                : "Arqueo creado correctamente"
            );
          }}
          isMobile={isMobile}
          showCustomAlert={showCustomAlert}
        />
      )}

      {/* Modal de Vista Detallada */}
      {showDetailModal && selectedArqueoDetail && (
        <ArqueoDetailModal
          arqueo={selectedArqueoDetail}
          proyectos={proyectos}
          cajas={cajas}
          constructora={constructora}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedArqueoDetail(null);
          }}
          isMobile={isMobile}
        />
      )}

      {/* Modal personalizado para alertas */}
      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal((prev) => ({ ...prev, isOpen: false }))}
        type={customModal.type}
        title={customModal.title}
        message={customModal.message}
        onConfirm={customModal.onConfirm}
        isMobile={isMobile}
      />

      {/* ✅ CSS OBLIGATORIO - ANIMACIONES UNIFICADAS */}
      <style jsx>{`
        /* Configuración anti-zoom */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones estándar */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .loading-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* Hover effects */
        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        /* Responsive desktop crítico */
        @media (min-width: 769px) {
          .header-section > div:first-child {
            flex-wrap: nowrap !important;
            min-height: 56px;
          }

          .button-hover {
            flex-shrink: 0 !important;
            white-space: nowrap !important;
            min-width: max-content !important;
          }

          .main-container {
            min-width: 1024px;
          }
        }
      `}</style>
    </div>
  );
}

// Modal para crear/editar arqueos
function ArqueoModal({
  constructora,
  usuario,
  arqueo,
  proyectos,
  cajas,
  onClose,
  onSave,
  isMobile,
  showCustomAlert,
}) {
  const [formData, setFormData] = useState({
    proyecto_id: "",
    caja_id: "",
    monto: "",
    tipoOperacion: "sumar", // 'sumar' o 'restar'
    descripcion: "",
    fecha: new Date().toISOString().split("T")[0],
    tipo_cambio: "1", // ✅ CAMPO TIPO DE CAMBIO
  });

  const [guardando, setGuardando] = useState(false);
  const [dolarOficial, setDolarOficial] = useState(null);
  const [loadingDolar, setLoadingDolar] = useState(false);

  // Cajas disponibles según el proyecto seleccionado
  const [cajasDisponibles, setCajasDisponibles] = useState([]);
  const [cajaSeleccionada, setCajaSeleccionada] = useState(null);

  const colorVista = getVistaColor();

  useEffect(() => {
    cargarDolarOficial();

    // Si estamos editando, cargar los datos del arqueo
    if (arqueo) {
      // Determinar el proyecto basado en la caja
      const proyectoId =
        arqueo.cajas?.proyectos?.id ||
        (arqueo.cajas?.tipo_caja === "empresa" ? "empresa" : "");

      setFormData({
        proyecto_id: proyectoId,
        caja_id: arqueo.caja_id,
        monto: Math.abs(parseFloat(arqueo.monto_ajuste)).toString(),
        tipoOperacion:
          parseFloat(arqueo.monto_ajuste) >= 0 ? "sumar" : "restar",
        descripcion: arqueo.descripcion || "",
        fecha: formatDateForInput(arqueo.fecha),
        tipo_cambio: arqueo.tipo_cambio ? arqueo.tipo_cambio.toString() : "1", // ✅ CARGAR TIPO DE CAMBIO
      });
    }
  }, [arqueo]);

  // Cargar cotización del dólar al abrir el modal
  useEffect(() => {
    cargarDolarOficial();
  }, []);

  // ✅ ACTUALIZAR TIPO DE CAMBIO AUTOMÁTICAMENTE CUANDO HAY DÓLAR OFICIAL
  useEffect(() => {
    if (dolarOficial && !arqueo) {
      setFormData((prev) => ({
        ...prev,
        tipo_cambio: dolarOficial.toString(),
      }));
    }
  }, [dolarOficial, arqueo]);

  // Actualizar cajas disponibles cuando cambia el proyecto
  useEffect(() => {
    if (formData.proyecto_id === "empresa") {
      actualizarCajasDisponibles(null);
    } else if (formData.proyecto_id) {
      const proyectoSeleccionado = proyectos.find(
        (p) => p.id === formData.proyecto_id
      );
      actualizarCajasDisponibles(proyectoSeleccionado);
    } else {
      setCajasDisponibles([]);
    }
  }, [formData.proyecto_id, cajas, proyectos]);

  // Actualizar caja seleccionada cuando cambia caja_id
  useEffect(() => {
    if (formData.caja_id) {
      const caja = cajasDisponibles.find((c) => c.id === formData.caja_id);
      setCajaSeleccionada(caja);
    } else {
      setCajaSeleccionada(null);
    }
  }, [formData.caja_id, cajasDisponibles]);

  const cargarDolarOficial = async () => {
    setLoadingDolar(true);
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
      const data = await response.json();
      const dolarVenta = data.venta;
      setDolarOficial(dolarVenta);
      console.log("Dólar oficial cargado:", dolarVenta);
    } catch (error) {
      console.error("Error cargando dólar oficial:", error);
      setDolarOficial(1000); // Fallback
    }
    setLoadingDolar(false);
  };

  const actualizarCajasDisponibles = (proyecto) => {
    let cajasProyecto = [];

    if (formData.proyecto_id === "empresa") {
      // Solo cajas de empresa
      cajasProyecto = cajas.filter((caja) => caja.tipo_caja === "empresa");
    } else if (proyecto) {
      // Cajas de empresa + cajas del proyecto seleccionado
      cajasProyecto = cajas.filter((caja) => {
        if (caja.tipo_caja === "empresa") return true;
        if (caja.proyecto_id === proyecto.id) return true;
        return false;
      });
    }

    // Ordenar: empresa primero, luego proyecto, luego mano obra
    const cajasOrdenadas = cajasProyecto.sort((a, b) => {
      if (a.tipo_caja === "empresa" && b.tipo_caja !== "empresa") return -1;
      if (a.tipo_caja !== "empresa" && b.tipo_caja === "empresa") return 1;
      if (a.tipo_caja === "proyecto" && b.tipo_caja === "mano_obra") return -1;
      if (a.tipo_caja === "mano_obra" && b.tipo_caja === "proyecto") return 1;
      return a.moneda.localeCompare(b.moneda);
    });

    setCajasDisponibles(cajasOrdenadas);
  };

  // ✅ FUNCIÓN PARA CALCULAR EQUIVALENTE EN LA OTRA MONEDA
  const calcularEquivalente = () => {
    if (!formData.monto || !formData.tipo_cambio || !cajaSeleccionada)
      return null;

    const monto = parseFloat(formData.monto);
    const tipoCambio = parseFloat(formData.tipo_cambio);

    if (cajaSeleccionada.moneda === "USD") {
      // Si es USD, mostrar equivalente en ARS
      const equivalenteARS = monto * tipoCambio;
      return `≈ ARS $${equivalenteARS.toLocaleString("es-AR", {
        maximumFractionDigits: 2,
      })}`;
    } else {
      // Si es ARS, mostrar equivalente en USD
      const equivalenteUSD = monto / tipoCambio;
      return `≈ USD $${equivalenteUSD.toLocaleString("en-US", {
        maximumFractionDigits: 2,
      })}`;
    }
  };

  // Función para calcular nuevo balance
  const calcularNuevoBalance = () => {
    if (!formData.monto || !cajaSeleccionada) return null;

    const balanceActual = parseFloat(cajaSeleccionada.balance_actual) || 0;
    const monto = parseFloat(formData.monto);
    const ajuste = formData.tipoOperacion === "sumar" ? monto : -monto;

    return balanceActual + ajuste;
  };

  const handleSave = async () => {
    // Validaciones
    if (
      !formData.proyecto_id ||
      !formData.caja_id ||
      !formData.monto ||
      !formData.fecha
    ) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor completa todos los campos obligatorios"
      );
      return;
    }

    if (parseFloat(formData.monto) <= 0) {
      showCustomAlert("error", "Error", "El monto debe ser mayor a 0");
      return;
    }

    if (!formData.tipo_cambio || parseFloat(formData.tipo_cambio) <= 0) {
      showCustomAlert("error", "Error", "El tipo de cambio debe ser mayor a 0");
      return;
    }

    setGuardando(true);

    try {
      // Calcular el monto de ajuste (positivo para suma, negativo para resta)
      const montoAjuste =
        formData.tipoOperacion === "sumar"
          ? parseFloat(formData.monto)
          : -parseFloat(formData.monto);

      const dataToSave = {
        constructora_id: constructora.id,
        caja_id: formData.caja_id,
        monto_ajuste: montoAjuste,
        descripcion: formData.descripcion.trim() || "Ajuste de caja",
        fecha: formData.fecha,
        tipo_cambio: parseFloat(formData.tipo_cambio), // ✅ GUARDAR TIPO DE CAMBIO
      };

      // Agregar campos de auditoría
      if (!arqueo) {
        // Creación: setear created_by y updated_by
        dataToSave.created_by = usuario.id;
        dataToSave.updated_by = usuario.id;
        console.log("🆕 Creando arqueo - created_by:", usuario.id, "updated_by:", usuario.id);
      } else {
        // Actualización: solo setear updated_by
        dataToSave.updated_by = usuario.id;
        console.log("✏️ Actualizando arqueo - updated_by:", usuario.id);
      }

      let result;
      if (arqueo) {
        // ACTUALIZAR ARQUEO EXISTENTE
        const ajusteAnterior = parseFloat(arqueo.monto_ajuste);
        const cajaAnteriorId = arqueo.caja_id;

        // Solo revertir SI cambió el monto o la caja
        if (
          montoAjuste !== ajusteAnterior ||
          formData.caja_id !== cajaAnteriorId
        ) {
          console.log("🔄 Cambió monto/caja - Revertir ajuste anterior");
          await actualizarBalanceCaja(cajaAnteriorId, -ajusteAnterior);
        } else {
          console.log("✅ No cambió monto/caja - Solo actualizar datos");
        }

        // Actualizar el arqueo
        result = await supabase
          .from("arqueos_caja")
          .update(dataToSave)
          .eq("id", arqueo.id)
          .select();

        if (result.error) {
          // Si hay error y se revirtió, restaurar
          if (
            montoAjuste !== ajusteAnterior ||
            formData.caja_id !== cajaAnteriorId
          ) {
            await actualizarBalanceCaja(cajaAnteriorId, ajusteAnterior);
          }
          throw result.error;
        }

        // Aplicar nuevo ajuste SI cambió algo
        if (
          montoAjuste !== ajusteAnterior ||
          formData.caja_id !== cajaAnteriorId
        ) {
          await actualizarBalanceCaja(formData.caja_id, montoAjuste);
        }
      } else {
        // CREAR NUEVO ARQUEO (mantener como está)
        result = await supabase
          .from("arqueos_caja")
          .insert([dataToSave])
          .select();

        if (!result.error) {
          await actualizarBalanceCaja(formData.caja_id, montoAjuste);
        }
      }

      const { data, error } = result;

      if (error) {
        console.error("Error guardando arqueo:", error);
        showCustomAlert("error", "Error", "Error al guardar: " + error.message);
      } else {
        onSave();
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }

    setGuardando(false);
  };

  const actualizarBalanceCaja = async (cajaId, montoAjuste) => {
    try {
      const { data: cajaData, error: errorCaja } = await supabase
        .from("cajas")
        .select("balance_actual")
        .eq("id", cajaId)
        .single();

      if (!errorCaja) {
        const balanceActual = parseFloat(cajaData.balance_actual) || 0;
        const nuevoBalance = balanceActual + parseFloat(montoAjuste);

        await supabase
          .from("cajas")
          .update({
            balance_actual: nuevoBalance,
            updated_at: new Date().toISOString(),
          })
          .eq("id", cajaId);

        console.log(`Caja actualizada: ${balanceActual} → ${nuevoBalance}`);
      }
    } catch (err) {
      console.error("Error en actualización de balance:", err);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "40px",
        animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: colorVista.gradient,
            color: "white",
            padding: isMobile ? "20px" : "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Scale size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {arqueo ? "Editar Arqueo" : "Nuevo Arqueo de Caja"}
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  opacity: 0.9,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {arqueo
                  ? "Modificar ajuste de balance existente"
                  : "Ajustar balance de una caja específica"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="button-hover"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "10px",
              padding: "8px 12px",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "600",
              flexShrink: 0,
              minWidth: "max-content",
              whiteSpace: "nowrap",
              touchAction: "manipulation",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <X size={16} />
          </button>
        </div>
        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "25px",
            overflowY: "auto",
          }}
        >
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Proyecto */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Building2 size={16} />
                Proyecto *
              </label>
              <CustomDropdown
                value={formData.proyecto_id}
                onChange={(value) =>
                  setFormData({ ...formData, proyecto_id: value, caja_id: "" })
                }
                options={[
                  { value: "", label: "Selecciona un proyecto" },
                  { value: "empresa", label: "🏢 Cajas de Empresa" },
                  ...proyectos.map((proyecto) => ({
                    value: proyecto.id,
                    label: `${proyecto.codigo} - ${proyecto.apellido_cliente} ${
                      proyecto.obra_finalizada ? "(Finalizado)" : "(Activo)"
                    }`,
                  })),
                ]}
                placeholder="Selecciona un proyecto"
                icon={Building2}
              />
            </div>

            {/* Caja */}
            {cajasDisponibles.length > 0 && (
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Wallet size={16} />
                  Caja *
                </label>
                <CustomDropdown
                  value={formData.caja_id}
                  onChange={(value) =>
                    setFormData({ ...formData, caja_id: value })
                  }
                  options={[
                    { value: "", label: "Selecciona una caja" },
                    ...cajasDisponibles.map((caja) => ({
                      value: caja.id,
                      label: `${caja.nombre} ${caja.moneda} (Saldo: ${
                        caja.moneda
                      } $${parseFloat(
                        caja.balance_actual || 0
                      ).toLocaleString()})`,
                    })),
                  ]}
                  placeholder="Selecciona una caja"
                  icon={Wallet}
                />
              </div>
            )}

            {/* Tipo de Operación */}
            {cajaSeleccionada && (
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Tipo de Ajuste *
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({ ...formData, tipoOperacion: "sumar" })
                    }
                    className="button-hover"
                    style={{
                      padding: "16px",
                      border: `2px solid ${
                        formData.tipoOperacion === "sumar"
                          ? "#10b981"
                          : "#e2e8f0"
                      }`,
                      borderRadius: "12px",
                      background:
                        formData.tipoOperacion === "sumar"
                          ? "#f0fdf4"
                          : "white",
                      color:
                        formData.tipoOperacion === "sumar"
                          ? "#10b981"
                          : "#64748b",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      touchAction: "manipulation",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <ArrowUp size={14} /> SUMAR
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({ ...formData, tipoOperacion: "restar" })
                    }
                    className="button-hover"
                    style={{
                      padding: "16px",
                      border: `2px solid ${
                        formData.tipoOperacion === "restar"
                          ? "#ef4444"
                          : "#e2e8f0"
                      }`,
                      borderRadius: "12px",
                      background:
                        formData.tipoOperacion === "restar"
                          ? "#fef2f2"
                          : "white",
                      color:
                        formData.tipoOperacion === "restar"
                          ? "#ef4444"
                          : "#64748b",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      touchAction: "manipulation",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <ArrowDown size={14} /> RESTAR
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Monto */}
            {cajaSeleccionada && (
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <DollarSign size={16} />
                  Monto a{" "}
                  {formData.tipoOperacion === "sumar" ? "Sumar" : "Restar"} (
                  {cajaSeleccionada.moneda}) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.monto}
                  onChange={(e) =>
                    setFormData({ ...formData, monto: e.target.value })
                  }
                  placeholder="0.00"
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    WebkitAppearance: "none",
                    appearance: "none",
                    touchAction: "manipulation",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#3b82f6";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(59, 130, 246, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f1f5f9";
                    e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                  }}
                />
              </div>
            )}

            {/* ✅ TIPO DE CAMBIO - SIEMPRE DESPUÉS DEL MONTO */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <DollarSign size={16} />
                Tipo de Cambio * {loadingDolar && "⏳"}
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.tipo_cambio}
                onChange={(e) =>
                  setFormData({ ...formData, tipo_cambio: e.target.value })
                }
                placeholder="1.00"
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  WebkitAppearance: "none",
                  appearance: "none",
                  touchAction: "manipulation",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
              {dolarOficial && (
                <p
                  style={{
                    fontSize: "12px",
                    color: "#059669",
                    margin: "4px 0 0 0",
                    fontWeight: "500",
                  }}
                >
                  💱 Dólar oficial: ${dolarOficial}
                </p>
              )}
              {/* ✅ MOSTRAR EQUIVALENTE EN LA OTRA MONEDA */}
              {formData.monto && calcularEquivalente() && (
                <p
                  style={{
                    fontSize: "12px",
                    color: "#0ea5e9",
                    margin: "2px 0 0 0",
                    fontWeight: "600",
                  }}
                >
                  {calcularEquivalente()}
                </p>
              )}
            </div>

            {/* Información adicional */}
            {cajaSeleccionada && formData.monto && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "16px",
                  background: "#f8fafc",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gap: "8px",
                    fontSize: "13px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      color: "#374151",
                    }}
                  >
                    <span>💰 Saldo actual:</span>
                    <span style={{ fontWeight: "600" }}>
                      {cajaSeleccionada.moneda} $
                      {parseFloat(
                        cajaSeleccionada.balance_actual || 0
                      ).toLocaleString()}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      color:
                        formData.tipoOperacion === "sumar"
                          ? "#059669"
                          : "#dc2626",
                      fontWeight: "600",
                    }}
                  >
                    <span>⚖️ Nuevo saldo:</span>
                    <span>
                      {cajaSeleccionada.moneda} $
                      {calcularNuevoBalance()?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Fecha */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Calendar size={16} />
                Fecha *
              </label>
              <input
                type="date"
                value={formData.fecha}
                onChange={(e) =>
                  setFormData({ ...formData, fecha: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  WebkitAppearance: "none",
                  appearance: "none",
                  touchAction: "manipulation",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>

            {/* Descripción */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FileText size={16} />
                Descripción (Motivo del ajuste)
              </label>
              <textarea
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
                placeholder="Ej: Corrección por error de carga, Ajuste por diferencia de inventario..."
                rows="3"
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  resize: "vertical",
                  WebkitAppearance: "none",
                  appearance: "none",
                  touchAction: "manipulation",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>
          </div>
        </div>
        {/* Footer */}
        <div
          style={{
            background: "#f8fafc",
            padding: isMobile ? "15px 20px" : "20px 25px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
            marginTop: "20px",
          }}
        >
          <button
            onClick={onClose}
            className="button-hover"
            style={{
              padding: isMobile ? "10px 16px" : "12px 20px",
              border: "2px solid #e2e8f0",
              borderRadius: "10px",
              background: "white",
              color: "#64748b",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <X size={16} />
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={
              guardando ||
              !formData.proyecto_id ||
              !formData.caja_id ||
              !formData.monto
            }
            className="button-hover"
            style={{
              padding: isMobile ? "10px 20px" : "12px 24px",
              border: "none",
              borderRadius: "10px",
              background:
                guardando ||
                !formData.proyecto_id ||
                !formData.caja_id ||
                !formData.monto
                  ? "#94a3b8"
                  : colorVista.gradient,
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor:
                guardando ||
                !formData.proyecto_id ||
                !formData.caja_id ||
                !formData.monto
                  ? "not-allowed"
                  : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            {guardando ? (
              <>
                <Loader2 size={16} className="loading-pulse" />
                Guardando...
              </>
            ) : (
              <>
                <Save size={16} />
                {arqueo ? "Actualizar Arqueo" : "Guardar Arqueo"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Arqueos;
